import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { Search, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { discountPrice, challengeFamily } from "../lib/format";
import { ModelDropdowns } from "../components/ChallengeTable";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Ticker from "../components/Ticker";
import FirmCard from "../components/FirmCard";
import FirmIcon from "../components/FirmIcon";
import DiscountBadge from "../components/DiscountBadge";
import Reveal, { SectionLabel } from "../components/Reveal";

const CATS = ["All", "CFD", "Futures", "Crypto"];
const SORTS = [
  { key: "rating", label: "Sort by Rank" },
  { key: "reviews", label: "Sort by Reviews" },
  { key: "name", label: "Sort A–Z" },
];

function ChallengesTable({ firms }) {
  const [family, setFamily] = useState("All");
  const [plan, setPlan] = useState("All");
  const all = useMemo(
    () => firms.flatMap((f) => (f.challenges || []).map((c) => ({ firm: f, ...c }))),
    [firms]
  );
  const rows = all.filter(
    (c) => (family === "All" || challengeFamily(c.model) === family) && (plan === "All" || c.model === plan)
  );

  const groups = useMemo(() => {
    if (plan !== "All") return null;
    const g = new Map();
    rows.forEach((r) => {
      const k = r.model || "Other";
      if (!g.has(k)) g.set(k, []);
      g.get(k).push(r);
    });
    return [...g.entries()];
  }, [rows, plan]);

  if (!all.length)
    return (
      <p className="rounded-2xl border border-line bg-surface p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-muted" data-testid="challenges-empty">
        No challenges listed for this filter yet.
      </p>
    );

  const renderRow = (r, i) => {
    const disc = discountPrice(r.price, r.discount_label || r.firm.discount_label);
    return (
      <tr key={`${r.firm.id}-${r.size}-${r.model}-${i}`} className="border-b border-line/50 text-muted transition-colors duration-200 hover:bg-signal/[0.04]"
        data-testid={`challenge-row-${r.firm.slug}-${r.size.replace(/[^a-z0-9]/gi, "")}-${(r.model || "x").replace(/[^a-z0-9]/gi, "")}`}>
        <td className="px-5 py-4">
          <Link to={`/firms/${r.firm.slug}`} className="flex items-center gap-3 text-white hover:text-signal">
            <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg border border-line bg-elevate"><FirmIcon name={r.firm.icon} size={13} logo={r.firm.logo} /></span>
            <span className="font-600">{r.firm.name}</span>
          </Link>
        </td>
        <td className="px-5 py-4 font-700 text-white">{r.size}</td>
        <td className="px-5 py-4">
          <span className="rounded-full border border-line bg-ink px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] text-signal">{r.model || "—"}</span>
        </td>
        <td className="px-5 py-4">
          {disc ? (
            <span>
              <span className="mr-2 text-muted/70 line-through">{r.price}</span>
              <span className="font-700 text-profit">{disc}</span>
            </span>
          ) : (
            <span className="text-signal">{r.price}</span>
          )}
        </td>
        <td className="px-5 py-4">{r.target}</td>
        <td className="px-5 py-4">{r.daily_dd}</td>
        <td className="px-5 py-4">{r.max_dd}</td>
        <td className="px-5 py-4">
          {r.firm.discount_code
            ? <DiscountBadge label={r.firm.discount_label} code={r.firm.discount_code} slug={`ch-${r.firm.slug}`} compact />
            : "—"}
        </td>
        <td className="px-5 py-4 text-right">
          <a href={r.firm.website_url} target="_blank" rel="noopener noreferrer sponsored" data-testid={`challenge-get-${r.firm.slug}-${i}`}
            className="btn-gradient inline-flex items-center gap-1.5 px-4 py-2 text-[10px] font-700 uppercase tracking-[0.12em] text-white">
            Get <ExternalLink size={10} />
          </a>
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end gap-4">
        <ModelDropdowns challenges={all} family={family} setFamily={setFamily} plan={plan} setPlan={setPlan} idPrefix="browser" />
        <span className="ml-auto hidden items-center pb-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted sm:flex">
          {rows.length} accounts
        </span>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-line" data-testid="challenges-table">
        <table className="w-full min-w-[940px] border-collapse font-mono text-xs">
          <thead>
            <tr className="border-b border-line bg-elevate text-left text-[10px] uppercase tracking-[0.2em] text-muted">
              <th className="px-5 py-4 font-500">Firm</th>
              <th className="px-5 py-4 font-500">Account</th>
              <th className="px-5 py-4 font-500">Model</th>
              <th className="px-5 py-4 font-500">Price</th>
              <th className="px-5 py-4 font-500">Target</th>
              <th className="px-5 py-4 font-500">Daily DD</th>
              <th className="px-5 py-4 font-500">Max DD</th>
              <th className="px-5 py-4 font-500">Code</th>
              <th className="px-5 py-4 font-500"></th>
            </tr>
          </thead>
          <tbody>
            {groups
              ? groups.map(([name, list]) => (
                  <Fragment key={name}>
                    <tr className="border-b border-line bg-signal/[0.07]" data-testid={`challenge-group-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <td colSpan={9} className="px-5 py-2.5 font-mono text-[10px] font-700 uppercase tracking-[0.2em] text-signal">
                        {name} <span className="ml-2 text-muted font-500">· {list.length} accounts</span>
                      </td>
                    </tr>
                    {list.map(renderRow)}
                  </Fragment>
                ))
              : rows.map(renderRow)}
          </tbody>
        </table>
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
        Struck prices are firm list prices — the club code on each row applies at checkout.
      </p>
    </div>
  );
}

export default function Firms() {
  const [firms, setFirms] = useState([]);
  const [view, setView] = useState("firms");
  const [cat, setCat] = useState("All");
  const [sort, setSort] = useState("rating");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/firms")
      .then(({ data }) => setFirms(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = [...firms];
    if (cat !== "All") list = list.filter((f) => (f.category || "CFD") === cat);
    if (query) list = list.filter((f) => f.name.toLowerCase().includes(query.toLowerCase()));
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    if (sort === "reviews") list.sort((a, b) => (b.review_count || 0) - (a.review_count || 0));
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    list.sort((a, b) => (b.pinned === true) - (a.pinned === true));
    return list;
  }, [firms, cat, sort, query]);

  const topSlug = firms.find((f) => f.pinned)?.slug || (firms.length ? firms.reduce((a, b) => (b.rating > a.rating ? b : a)).slug : null);
  const challengeCount = firms.reduce((s, f) => s + (f.challenges?.length || 0), 0);

  return (
    <div data-testid="firms-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="terminal-grid absolute inset-0 opacity-50" />
        <div className="absolute -right-24 top-0 h-72 w-72 rounded-full bg-signal/15 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <Reveal>
            <SectionLabel>The Index</SectionLabel>
            <h1 className="mt-3 font-display text-5xl font-900 tracking-tight text-white sm:text-6xl">
              Prop firm <span className="text-gradient">ratings</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted">
              {firms.length} firms · {challengeCount} challenge accounts · member codes and
              verified payout reviews on every desk.
            </p>
          </Reveal>

          <div className="mt-10 grid max-w-md grid-cols-2 gap-2 rounded-2xl border border-line bg-ink/60 p-2" data-testid="view-toggle">
            {[
              { key: "firms", label: "Firms" },
              { key: "challenges", label: "Challenges" },
            ].map((v) => (
              <button key={v.key} onClick={() => setView(v.key)} data-testid={`view-${v.key}`}
                className={`rounded-xl px-4 py-2.5 font-display text-sm font-700 transition-all duration-200 ${
                  view === v.key ? "bg-signal text-white shadow-[0_0_18px_rgba(0,212,255,0.35)]" : "text-muted hover:text-white"
                }`}>
                {v.label}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl border border-line bg-ink/60 p-2 sm:grid-cols-4" data-testid="filter-cats">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c.toLowerCase()}`}
                className={`rounded-xl px-4 py-2.5 font-display text-sm font-700 transition-all duration-200 ${
                  cat === c ? "bg-signal text-white shadow-[0_0_18px_rgba(0,212,255,0.35)]" : "text-muted hover:text-white"
                }`}>
                {c}
              </button>
            ))}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-[240px_1fr]">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="firms-sort"
              className="rounded-xl border border-line bg-ink px-4 py-3 font-mono text-xs text-white outline-none focus:border-signal">
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <div className="relative">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="firms-search"
                placeholder="Search firm name..."
                className="w-full rounded-xl border border-line bg-ink py-3 pl-11 pr-4 text-sm text-white outline-none transition-colors focus:border-signal" />
            </div>
          </div>
        </div>
      </section>

      <Ticker firms={firms} />

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="firms-loading">
            LOADING TAPE<span className="animate-blink">…</span>
          </p>
        ) : view === "challenges" ? (
          <ChallengesTable firms={filtered} />
        ) : filtered.length === 0 ? (
          <p className="rounded-2xl border border-line bg-surface p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-muted" data-testid="firms-empty">
            No firms match this filter.
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="firms-grid">
            {filtered.map((firm, i) => (
              <FirmCard key={firm.id} firm={firm} index={i} showCrown={firm.slug === topSlug} />
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
